import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiClient } from '../apiClient';
import { API_ENDPOINTS } from '../constants';
import { Category } from '../types';
import { extractApiErrorMessage } from '../utils/errorHandler';

interface CategoriesState {
  items: Category[];
  loading: boolean;
  error: string | null;
}

const initialState: CategoriesState = {
  items: [],
  loading: false,
  error: null
};

export const fetchCategories = createAsyncThunk<Category[]>(
  'categories/fetchAll',
  async () => {
    const response = await apiClient.get(`${API_ENDPOINTS.CATEGORIES}`);
    return response.data as Category[];
  }
);

interface SaveCategoryPayload {
  id?: string;
  name: string;
  description: string;
  parent?: string | null;
}

export const saveCategory = createAsyncThunk<Category, SaveCategoryPayload, { rejectValue: string }>(
  'categories/save',
  async (payload, { rejectWithValue }) => {
    try {
      if (payload.id) {
        const { id, ...data } = payload;
        const response = await apiClient.put(
          `${API_ENDPOINTS.CATEGORIES}/${id}`,
          data
        );
        return response.data as Category;
      }
      const response = await apiClient.post(`${API_ENDPOINTS.CATEGORIES}`, payload);
      return response.data as Category;
    } catch (error) {
      return rejectWithValue(extractApiErrorMessage(error, 'Failed to save category'));
    }
  }
);

export const deleteCategory = createAsyncThunk<string, string>(
  'categories/delete',
  async (id) => {
    await apiClient.delete(`${API_ENDPOINTS.CATEGORIES}/${id}`);
    return id;
  }
);

const categoriesSlice = createSlice({
  name: 'categories',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load categories';
      })
      .addCase(saveCategory.fulfilled, (state, action) => {
        const idx = state.items.findIndex((c) => c._id === action.payload._id);
        if (idx >= 0) {
          state.items[idx] = action.payload;
        } else {
          state.items.push(action.payload);
        }
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.items = state.items.filter((c) => c._id !== action.payload);
      });
  }
});

export default categoriesSlice.reducer;

